import ReviewsFilterInput from '@app/model/review/input/ReviewsFilterInput';
import { GraphQLExecutor } from '@app/model/_common/service/GraphQLExecutor';
import {
	FILTER_REVIEWS_QUERY, FIND_GROUP_REVIEWS_STATE_FOR_PERIOD_QUERY,
	FIND_REVIEW_CATEGORIES_QUERY,
	FIND_REVIEWS_INSIGHTS_DELIVERY_SCHEDULES_QUERY,
	FIND_REVIEWS_FOR_TEXT_CATEGORY_QUERY,
	FIND_REVIEWS_MONTHLY_DIGEST_QUERY, FIND_REVIEWS_STATE_FOR_PERIOD_QUERY, FIND_REVIEWS_WEEKLY_DIGEST_QUERY,
	MARK_REVIEW_AS_ANSWERED,
	MARK_REVIEW_AS_UNANSWERED,
	MARK_REVIEW_AS_VIEWED_BY_USER_MUTATION,
	REMOVE_REVIEW_INTERNAL_NOTES_MUTATION,
	SAVE_REVIEW_INTERNAL_NOTES_MUTATION,
	SAVE_REVIEWS_INSIGHTS_DELIVERY_SCHEDULE_MUTATION,
	TRANSLATE_REVIEW_MUTATION,
} from '@app/model/review/service/ReviewGraphQLQueries';
import ReviewOutput from '@app/model/review/output/ReviewOutput';
import { QueryClient } from '@tanstack/react-query';
import { ReviewsQueryKeys } from '@app/model/review/service/ReviewsQueryKeys';
import Review from '@app/model/review/Review';
import ReviewHandlingSession from '@app/model/review/ReviewHandlingSession';
import ReviewInternalNotesInput from '@app/model/review/input/ReviewInternalNotesInput';
import ReviewCategoryOutput from '@app/model/review/output/ReviewCategoryOutput';
import ReviewsStateForPeriod from '@app/model/review/state/ReviewsStateForPeriod';
import GroupReviewsStateForPeriod from '@app/model/review/state/GroupReviewsStateForPeriod';
import { ReviewsWeeklyDigest } from '@app/model/review/digest/weekly/ReviewsWeeklyDigest';
import { ReviewsMonthlyDigest } from '@app/model/review/digest/montly/ReviewsMonthlyDigest';
import ReviewsStateFilter from '@app/model/review/input/ReviewsStateFilter';
import ReportsDeliverySchedule from '@app/model/review/delivery/ReportsDeliverySchedule';
import ReviewsInsightsDeliveryScheduleInput
	from '@app/model/review/delivery/input/ReviewsInsightsDeliveryScheduleInput';

export class ReviewService {


	public static findReviewCategories(): Promise<ReviewCategoryOutput[]> {
		return GraphQLExecutor.execute<ReviewCategoryOutput[]>(FIND_REVIEW_CATEGORIES_QUERY);
	}

	public static async findReviewsWeeklyDigest(companyHotelId: number): Promise<ReviewsWeeklyDigest | null> {
		const result = await  GraphQLExecutor.execute<ReviewsWeeklyDigest>(FIND_REVIEWS_WEEKLY_DIGEST_QUERY, { companyHotelId });
		return result ? new ReviewsWeeklyDigest(result) : null;
	}

	public static async findReviewsMonthlyDigest(companyHotelId: number): Promise<ReviewsMonthlyDigest | null> {
		const result = await  GraphQLExecutor.execute<ReviewsMonthlyDigest>(FIND_REVIEWS_MONTHLY_DIGEST_QUERY, { companyHotelId });
		return result ? new ReviewsMonthlyDigest(result) : null;
	}

	public static async findReviewsInsightsDeliverySchedules(companyHotelId: number): Promise<ReportsDeliverySchedule[]> {
		const result = await GraphQLExecutor.execute<ReportsDeliverySchedule[]>(FIND_REVIEWS_INSIGHTS_DELIVERY_SCHEDULES_QUERY, { companyHotelId });
		return result ? result.map(data => new ReportsDeliverySchedule(data)) : [];
	}

	public static async saveReviewsInsightsDeliverySchedule(input: ReviewsInsightsDeliveryScheduleInput): Promise<ReportsDeliverySchedule> {
		const result = await GraphQLExecutor.execute<ReportsDeliverySchedule>(SAVE_REVIEWS_INSIGHTS_DELIVERY_SCHEDULE_MUTATION, { input });
		return new ReportsDeliverySchedule(result);
	}

	public static async filterReviews(filter: ReviewsFilterInput): Promise<ReviewOutput[]> {
		const result = await GraphQLExecutor.execute<ReviewOutput[]>(FILTER_REVIEWS_QUERY, { filter });
		return result ? result.map(data => new ReviewOutput(data)) : [];
	}


	public static async findReviewsForTextCategory(companyHotelId: number, hotelId: number, categoryId: number): Promise<Review[]> {
		const result = await GraphQLExecutor.execute<Review[]>(FIND_REVIEWS_FOR_TEXT_CATEGORY_QUERY, {
			companyHotelId,
			hotelId,
			categoryId,
		});
		return result ? result.map(data => new Review(data)) : [];
	}

	public static async markReviewAsViewedByUser(reviewId: number, filter: ReviewsFilterInput, queryClient: QueryClient): Promise<ReviewHandlingSession> {
		const result = await GraphQLExecutor.execute<ReviewHandlingSession>(MARK_REVIEW_AS_VIEWED_BY_USER_MUTATION, { reviewId });
		const sessionInstance = result ? new ReviewHandlingSession(result) : null;

		if (sessionInstance) {
			this.replaceInCache(reviewId, filter, queryClient, { session: sessionInstance });
		}

		return result;
	}

	public static async saveReviewInternalNotes(input: ReviewInternalNotesInput, filter: ReviewsFilterInput, queryClient: QueryClient): Promise<ReviewHandlingSession> {
		const result = await GraphQLExecutor.execute<ReviewHandlingSession>(SAVE_REVIEW_INTERNAL_NOTES_MUTATION, { input });
		const sessionInstance = result ? new ReviewHandlingSession(result) : null;

		if (sessionInstance) {
			this.replaceInCache(input.reviewId, filter, queryClient, { session: sessionInstance });
		}

		return result;
	}

	public static async removeReviewInternalNotes(reviewId: number, logId: number, filter: ReviewsFilterInput, queryClient: QueryClient): Promise<ReviewHandlingSession> {
		const result = await GraphQLExecutor.execute<ReviewHandlingSession>(REMOVE_REVIEW_INTERNAL_NOTES_MUTATION, {
			reviewId,
			logId,
		});
		const sessionInstance = result ? new ReviewHandlingSession(result) : null;

		if (sessionInstance) {
			this.replaceInCache(reviewId, filter, queryClient, { session: sessionInstance });
		}

		return result;
	}

	public static async markReviewAsAnswered(reviewId: number, filter: ReviewsFilterInput, queryClient: QueryClient): Promise<ReviewHandlingSession> {
		const result = await GraphQLExecutor.execute<ReviewHandlingSession>(MARK_REVIEW_AS_ANSWERED, { reviewId });
		const sessionInstance = result ? new ReviewHandlingSession(result) : null;

		if (sessionInstance) {
			this.replaceInCache(reviewId, filter, queryClient, { session: sessionInstance });
		}

		return result;
	}

	public static async markReviewAsUnanswered(reviewId: number, filter: ReviewsFilterInput, queryClient: QueryClient): Promise<ReviewHandlingSession> {
		const result = await GraphQLExecutor.execute<ReviewHandlingSession>(MARK_REVIEW_AS_UNANSWERED, { reviewId });
		const sessionInstance = result ? new ReviewHandlingSession(result) : null;

		if (sessionInstance) {
			this.replaceInCache(reviewId, filter, queryClient, { session: sessionInstance });
		}

		return result;
	}

	public static async translateReview(reviewId: number, filter: ReviewsFilterInput, queryClient: QueryClient): Promise<Review> {
		const result = await GraphQLExecutor.execute<any>(TRANSLATE_REVIEW_MUTATION, { reviewId });
		const reviewInstance = result ? new Review(result) : null;

		if (reviewInstance) {
			this.replaceInCache(reviewId, filter, queryClient, { review: reviewInstance });
		}

		return result;
	}

	private static replaceInCache = (reviewId: number, filter: ReviewsFilterInput, queryClient: QueryClient, prop: Partial<ReviewOutput>) => {
		// Update the cache by replacing the review in the filtered reviews list
		queryClient.setQueryData<ReviewOutput[]>(
			ReviewsQueryKeys.filter(filter),
			(oldData) => {
				if (!oldData) return oldData;

				return oldData.map(reviewOutput =>
					reviewOutput.review.id === reviewId
						? new ReviewOutput({ ...reviewOutput, ...prop })
						: reviewOutput,
				);
			},
		);
	};

	public static async findReviewStatesForPeriod(companyHotelId: number, filter?: ReviewsStateFilter) {
		const result = await GraphQLExecutor.execute<ReviewsStateForPeriod>(FIND_REVIEWS_STATE_FOR_PERIOD_QUERY, { companyHotelId, filter });
		return !!result ? new ReviewsStateForPeriod(result) : null;
	}

	public static async findGroupReviewsStateForPeriod(filter?: ReviewsStateFilter) {
		const result = await GraphQLExecutor.execute<GroupReviewsStateForPeriod>(FIND_GROUP_REVIEWS_STATE_FOR_PERIOD_QUERY, {filter});
		return !!result ? new GroupReviewsStateForPeriod(result) : null;
	}
}
